import React, {useMemo, useState} from 'react';
import {useMutation, useQuery} from "react-query";
import * as Yup from "yup";
import {useFormik} from "formik";

import {Button, Input, ModalRequest} from "../common";
import {ALERT_TYPES, BUTTON_TYPES, INPUT_PLACEHOLDERS, INPUT_TYPES, IUser, LS_KEYS, ROUTES} from "../../types";
import {DeleteIcon, DownloadIcon} from "../icons";
import {WarningModal} from "./index";
import {useAlert} from "../common/Alert";
import {useNavigate} from "react-router-dom";
import {phoneRegex} from "../../utils/regex";

const FILES_DATA = [
  {
    title: 'Форма присоединения для ИП',
    href: '/pdf/forma_ip_20211201.docx',
  },
  {
    title: 'Форма присоединения для ЮЛ',
    href: '/pdf/forma_ooo_20211201.docx',
  },
  {
    title: 'Форма присоединения для Самозанятого',
    href: '/pdf/forma_fl_20211201.docx',
  },
  {
    title: 'Пример для заполнения',
    href: '/pdf/forma_example.docx',
  },
]

interface ISchema {
  [key: string]: string;
  fio: string;
  username: string;
  email: string;
  phone: string;
}

const AccountSettingsModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const navigate = useNavigate()
  const { setAlert } = useAlert();
  const { data, refetch }:
    { data: IUser | undefined; refetch?: any} = useQuery(["userInfo"], { refetchOnWindowFocus: false });

  const [file, setFile] = useState<any>(null);
  const [isOpenWarningModal, setIsOpenWarningModal] = useState<boolean>(false);

  const sendUserForm = useMutation(["sendUserForm"], {
    onMutate: (data: { form: any }) => {},
    onSuccess: () => {
      setAlert('Форма успешно добавлена!', ALERT_TYPES.SUCCESS);
    },
    onError: (e: any) => {
      const errorText: string = `${Object.values(e.response.data).flat(2)?.[0]}`;
      setAlert(errorText, ALERT_TYPES.ERROR);
    }
  });

  const updateUserInfo = useMutation(["updateUserInfo"], {
    onMutate: (data: {
      username?: string;
      fio?: string;
      email?: string;
      phone_number?: string;
      password?: string;
    }) => {},
    onSuccess: () => {
      setAlert('Информация успешно обновлена!', ALERT_TYPES.SUCCESS);
      refetch()
      onClose();
    },
    onError: (e: any) => {
      const errorText: string = `${Object.values(e.response.data).flat(2)?.[0]}`;
      setAlert(errorText, ALERT_TYPES.ERROR);
    }
  })

  const onCloseWarningModal = () => setIsOpenWarningModal(false);

  const handleUploadFile = (e: any) => {
    setFile(e.target.files[0]);
  }

  const handleDeleteFile = () => setFile(null);

  const validationSchema: Yup.Schema<ISchema> = Yup.object().shape({
    fio: Yup.string().required('Поле не может быть пустым'),
    username: Yup.string().required('Поле не может быть пустым'),
    email: Yup.string().required('Поле не может быть пустым'),
    phone: Yup.string().required('Поле не может быть пустым')?.matches(phoneRegex, 'Невалидный номер телефона'),
  });

  const submitForm = async (values: any) => {
    const {
      username = '',
      fio = '',
      email = '',
      phone = '',
    } = values;

    const formData = new FormData();
    formData.append('form', file);

    const formDataObj = Object.fromEntries(formData.entries());

    file && await sendUserForm.mutate({ form: formDataObj?.form });

    await updateUserInfo.mutate({
      username,
      fio,
      email,
      phone_number: phone,
    })
  }

  const formik = useFormik({
    initialValues: {
      fio: data?.fio || '',
      username: data?.username || '',
      email: data?.email || '',
      phone: data?.phone_number || '',
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: submitForm
  });

  const { touched, errors, handleSubmit } = formik;

  return (
    <ModalRequest open={open} onClose={() => setIsOpenWarningModal(true)} title="Настройки аккаунта">
      <>
        <WarningModal
          open={isOpenWarningModal}
          onClose={onClose}
          onCloseWarningModal={onCloseWarningModal}
        />
        <p className="text-[14px] leading-[24px] text-black font-medium mb-1">ФИО</p>
        <Input
          id="fio"
          name="fio"
          defaultValue={data?.fio}
          className="w-full"
          placeholder="ФИО"
          onChange={formik.handleChange}
          error={touched?.fio ? errors?.fio : false}
        />

        <p className="text-[14px] leading-[24px] text-black font-medium mt-4 mb-1">Логин</p>
        <Input
          id="username"
          name="username"
          defaultValue={data?.username}
          className="w-full"
          placeholder="Логин"
          onChange={formik.handleChange}
          error={touched?.username ? errors?.username : false}
        />

        <p className="text-[14px] leading-[24px] text-black font-medium mt-4 mb-1">Адрес электронной почты</p>
        <Input
          id="email"
          name="email"
          defaultValue={data?.email}
          className="w-full"
          placeholder="Email"
          onChange={formik.handleChange}
          error={touched?.email ? errors?.email : false}
        />

        <p className="text-[14px] leading-[24px] text-black font-medium mt-4 mb-1">Номер телефона</p>
        <Input
          id="phone"
          name="phone"
          className="w-full"
          defaultValue={data?.phone_number}
          placeholder={INPUT_PLACEHOLDERS.PHONE}
          onChange={formik.handleChange}
          error={touched?.phone ? errors?.phone : false}
        />
        <span className="block mt-4 text-mainBlack mb-1 text-[14px] leading-[24px] font-medium">Прикрепить согласие на обработку</span>

        <div className="inline-flex flex-col">
          {FILES_DATA?.map(i => (
            <a
              key={i?.title}
              href={i?.href}
              className="mb-3 inline-block text-[14px] leading-[24px] text-sky-900 hover:opacity-70 duration-150 ease-in"
            >{i?.title}</a>
          ))}
        </div>

        {
          !!file
            ? (
              <>
                <p className="text-mainBlack mt-4">Загруженный файл: </p>
                <div className="flex items-center gap-5 mt-2">
                  <span className="text-mainBlack">{file?.name}</span>
                  <span
                    className="cursor-pointer hover:scale-110 duration-150 ease-in"
                    onClick={handleDeleteFile}
                  >
                  <DeleteIcon />
                </span>
                </div>
              </>
            )
            : (
              <>
                <div className="relative flex gap-1">
                  <input type={INPUT_TYPES.FILE} className="absolute opacity-0 block w-full" onChange={handleUploadFile} />
                  <span
                    className="block w-full border border-mainBlack/10 rounded bg-white p-2 text-mainBlack/75 text-[14px] leading-[16px]"
                  >Выберите файл</span>
                  <span className="px-2 rounded bg-mainBlack/10 flex items-center">
                    <DownloadIcon />
                  </span>
                  <span
                    className="text-black text-[14px] leading-[16px] font-medium bg-mainBlack/10 flex items-center px-4 rounded"
                  >Выбрать</span>
                </div>
              </>
            )
        }

        <div className="mt-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button title={'Сохранить'} type={BUTTON_TYPES.SECONDARY} onClick={handleSubmit} />
            <Button title={'Отмена'} type={BUTTON_TYPES.GRAY} onClick={onClose} className="ml-2" />
          </div>
        </div>
      </>
    </ModalRequest>
  )
};

export default AccountSettingsModal;
